<template>
  <div class="fan-external-editor space-y-5">
    <!-- Switch mode -->
    <section class="fan-external-editor__section">
      <h4 class="fan-external-editor__section-title">{{ tt('Switch mode', 'Način stikala') }}</h4>
      <div class="fan-external-editor__mode-grid">
        <button
          v-for="option in MODE_OPTIONS"
          :key="option.value"
          type="button"
          class="fan-external-editor__mode"
          :class="{ 'fan-external-editor__mode--active': config.switch_mode === option.value }"
          @click="update({ switch_mode: option.value })">
          <ControlIcon :name="option.icon" size="1.125rem" />
          <span class="fan-external-editor__mode-label">{{ tt(option.label.en, option.label.sl) }}</span>
          <span class="fan-external-editor__mode-desc">{{ tt(option.desc.en, option.desc.sl) }}</span>
        </button>
      </div>
    </section>

    <!-- On speed (toggle / follow / momentary) -->
    <section
      v-if="config.switch_mode === 'toggle' || config.switch_mode === 'follow' || config.switch_mode === 'momentary'"
      class="fan-external-editor__section">
      <h4 class="fan-external-editor__section-title">{{ tt('On speed', 'Hitrost ob vklopu') }}</h4>
      <div class="fan-external-editor__slider">
        <div class="fan-external-editor__slider-head">
          <span class="fan-external-editor__label">{{
            tt('Speed when the fan is on', 'Hitrost, ko je ventilator vklopljen')
          }}</span>
          <span class="fan-external-editor__slider-value">{{ config.on_speed }}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.on_speed"
          @input="update({ on_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>
      <p class="fan-external-editor__hint">
        {{
          config.switch_mode === 'toggle' || config.switch_mode === 'momentary'
            ? tt(
                'Each press toggles the fan between off and this speed.',
                'Vsak pritisk preklopi ventilator med izklopljenim stanjem in to hitrostjo.',
              )
            : tt(
                'The fan follows the switch: on → this speed, off → 0.',
                'Ventilator sledi stikalu: vklop → ta hitrost, izklop → 0.',
              )
        }}
      </p>
    </section>

    <!-- Speed cycle -->
    <section v-if="config.switch_mode === 'speed_cycle'" class="fan-external-editor__section">
      <h4 class="fan-external-editor__section-title">{{ tt('Speed cycle', 'Cikel hitrosti') }}</h4>
      <label class="fan-external-editor__number">
        <span>{{ tt('Number of speed levels', 'Število stopenj hitrosti') }}</span>
        <input
          type="number"
          min="1"
          max="10"
          :value="config.speed_steps"
          @change="
            update({ speed_steps: clampNum(Math.round(Number(($event.target as HTMLInputElement).value)), 1, 10) })
          " />
        <span></span>
      </label>
      <p class="fan-external-editor__hint">
        {{ tt('Each press advances the fan through', 'Vsak pritisk pomakne ventilator skozi') }}
        {{ config.speed_steps }}
        {{ tt('speed levels up to 100%, then back to off', 'stopenj hitrosti do 100 %, nato nazaj na izklop') }}
        ({{ cyclePreview }}).
      </p>
    </section>

    <!-- Up / Down buttons -->
    <section v-if="config.switch_mode === 'speed_up_down'" class="fan-external-editor__section">
      <h4 class="fan-external-editor__section-title">{{ tt('Up / Down buttons', 'Gumba gor / dol') }}</h4>
      <div class="fan-external-editor__grid">
        <label class="fan-external-editor__number">
          <span>{{ tt('Min speed', 'Najmanjša hitrost') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.min_speed"
            @change="update({ min_speed: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
        <label class="fan-external-editor__number">
          <span>{{ tt('Max speed', 'Največja hitrost') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.max_speed"
            @change="update({ max_speed: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
        <label class="fan-external-editor__number">
          <span>{{ tt('Speed step', 'Korak hitrosti') }}</span>
          <input
            type="number"
            min="1"
            max="100"
            :value="config.speed_step_percent"
            @change="
              update({ speed_step_percent: clampNum(Number(($event.target as HTMLInputElement).value), 1, 100) })
            " />
          <span>%</span>
        </label>
      </div>
      <p class="fan-external-editor__hint">
        {{
          tt(
            'Pressing the speed up button from off starts the fan at the min speed; pressing speed down at the min speed turns it off.',
            'Pritisk na gumb za hitreje iz izklopljenega stanja zažene ventilator z najmanjšo hitrostjo; pritisk na počasneje pri najmanjši hitrosti ga izklopi.',
          )
        }}
      </p>
    </section>

    <!-- Debounce -->
    <section class="fan-external-editor__section">
      <h4 class="fan-external-editor__section-title">{{ tt('Debounce', 'Preprečevanje odboja') }}</h4>
      <label class="fan-external-editor__number">
        <span>{{ tt('Debounce time', 'Čas preprečevanja odboja') }}</span>
        <input
          type="number"
          min="0"
          max="500"
          step="10"
          :value="config.debounce_ms"
          @change="update({ debounce_ms: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })" />
        <span>ms</span>
      </label>
      <p class="fan-external-editor__hint">
        {{
          tt(
            'Rapid changes within this window are ignored (e.g. switch bounce).',
            'Hitre spremembe znotraj tega okna se prezrejo (npr. odboj stikala).',
          )
        }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  FanExternalControlConfig,
  FanExternalSwitchMode,
  normalizeFanExternalControlConfig,
} from '../../../utils/fanAutomation';
import type { HubConfigBag } from '../deviceWidgetTypes';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';
import type { Bilingual } from '../../../config/bilingual';

const props = defineProps<{
  modelValue: HubConfigBag;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: HubConfigBag): void;
}>();

/**
 * The mode cards' words and drawn glyphs (card t_a8249a54): both languages in
 * the data, one picked at render; the icons are names of the drawn
 * `ControlIcon` set — a switch (`toggle`), a cycle (`refresh`), a follow link
 * (`link`), a press (`tap`) and up/down buttons (`up-down`).
 */
const MODE_OPTIONS: Array<{
  value: FanExternalSwitchMode;
  label: Bilingual;
  icon: string;
  desc: Bilingual;
}> = [
  {
    value: 'toggle',
    label: { en: 'Toggle', sl: 'Preklop' },
    icon: 'toggle',
    desc: { en: 'Single button', sl: 'En gumb' },
  },
  {
    value: 'speed_cycle',
    label: { en: 'Speed cycle', sl: 'Cikel hitrosti' },
    icon: 'refresh',
    desc: { en: 'Cycle levels', sl: 'Kroženje stopenj' },
  },
  {
    value: 'follow',
    label: { en: 'Follow', sl: 'Sledenje' },
    icon: 'link',
    desc: { en: 'Fan = switch', sl: 'Ventilator = stikalo' },
  },
  {
    value: 'momentary',
    label: { en: 'Momentary', sl: 'Momentarno' },
    icon: 'tap',
    desc: { en: 'Press toggles', sl: 'Dotik preklopi' },
  },
  {
    value: 'speed_up_down',
    label: { en: 'Up / Down', sl: 'Gor / Dol' },
    icon: 'up-down',
    desc: { en: 'Two buttons', sl: 'Dva gumba' },
  },
];

const config = computed<FanExternalControlConfig>(() => normalizeFanExternalControlConfig(props.modelValue));

const cyclePreview = computed(() => {
  const steps = Math.max(1, config.value.speed_steps);
  const levels = Array.from({ length: steps + 1 }, (_, i) => Math.round((100 * i) / steps));
  return levels.join(' → ');
});

function commit(next: FanExternalControlConfig) {
  emit('update:modelValue', JSON.parse(JSON.stringify(next)) as HubConfigBag);
}

function update(patch: Partial<FanExternalControlConfig>) {
  commit({ ...config.value, ...patch });
}

function clampNum(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
</script>

<style scoped>
.fan-external-editor {
  width: 100%;
}

.fan-external-editor__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
  margin-bottom: 8px;
}

.fan-external-editor__hint {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-top: 6px;
}

.fan-external-editor__mode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.fan-external-editor__mode {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  text-align: left;
  transition: border-color 0.15s;
}

.fan-external-editor__mode--active {
  border-color: rgb(56, 189, 248);
  background: rgba(56, 189, 248, 0.08);
}

.fan-external-editor__mode--active .fan-external-editor__mode-label {
  color: rgb(14, 165, 233);
}

.fan-external-editor__mode-label {
  font-size: 12px;
  font-weight: 600;
  color: rgb(51, 65, 85);
}

.dark .fan-external-editor__mode-label {
  color: rgb(226, 232, 240);
}

.fan-external-editor__mode-desc {
  font-size: 10px;
  color: rgb(148, 163, 184);
}

.fan-external-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.fan-external-editor__number {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 6px 8px;
}

.fan-external-editor__number input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  text-align: right;
}

.fan-external-editor__slider {
  margin-top: 4px;
}

.fan-external-editor__slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fan-external-editor__label {
  font-size: 11px;
  color: rgb(148, 163, 184);
}

.fan-external-editor__slider-value {
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.fan-external-editor__slider input[type='range'] {
  width: 100%;
  accent-color: rgb(56, 189, 248);
}
</style>
